import { motion } from 'framer-motion';
import { Brain, Zap, LineChart } from 'lucide-react';
import { ServiceFeature } from '@/types/service.types';
import Image from 'next/image';

interface ServiceFeaturesProps {
	features: ServiceFeature[];
	index: number;
	order: number;
}

export function ServiceFeatures({ features, index, order }: ServiceFeaturesProps) {
	return (
		<motion.div
			className={`flex flex-col space-y-4 lg:max-w-xl mx-auto lg:order-${order}`}
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: '-100px' }}
			transition={{
				duration: 0.5,
				ease: [0.2, 0.65, 0.3, 0.9],
				delay: index * 0.1,
			}}
		>
			<div className="flex flex-col items-stretch justify-center space-y-4">
				{features.map((feature, featureIndex) => (
					<motion.div
						key={feature.name}
						initial={{ opacity: 0, y: 10 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, margin: '-50px' }}
						transition={{
							duration: 0.4,
							ease: [0.2, 0.65, 0.3, 0.9],
							delay: featureIndex * 0.08,
						}}
						className="space-card rounded-xl p-5"
					>
						<h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
							{feature.icon ? (
								<feature.icon className="w-5 h-5 text-red-500" />
							) : (
								<>
									{featureIndex === 0 && <Zap className="w-5 h-5 text-red-500" />}
									{featureIndex === 1 && <LineChart className="w-5 h-5 text-red-500" />}
									{featureIndex === 2 && <Brain className="w-5 h-5 text-red-500" />}
								</>
							)}
							{feature.name}
						</h3>
						<p className="text-muted-foreground mb-3">{feature.description}</p>
						<ul className="space-y-1.5">
							{feature.capabilities.map((capability, capIndex) => (
								<motion.li
									key={capability}
									className="flex items-center text-sm"
									initial={{ opacity: 0, x: -5 }}
									whileInView={{ opacity: 1, x: 0 }}
									viewport={{ once: true, margin: '-20px' }}
									transition={{
										duration: 0.3,
										ease: [0.2, 0.65, 0.3, 0.9],
										delay: featureIndex * 0.05 + capIndex * 0.03,
									}}
								>
									<Image
										src="/images/logo/SVG/Icon/JCW_Logo_Updated_2026-09.svg"
										alt="JCW Icon"
										width={16}
										height={16}
										className="mr-2"
									/>
									{capability}
								</motion.li>
							))}
						</ul>
					</motion.div>
				))}
			</div>
		</motion.div>
	);
}
